import { CollectionFile } from "shared/types/files";
import { FeedMedia } from "./types";
import { StockImage } from "api/media-library";

export const feedMediaToCollectionFile = (
  feedItem: FeedMedia
): CollectionFile => {
  return {
    _id: feedItem.id,
    name: feedItem.caption || "Untitled",
    thumbnail: feedItem.thumbnail || feedItem.media_url,
    downloadUrl: feedItem.media_url,
    meta: {
      isVideo: feedItem.media_type === "VIDEO",
      isImage: feedItem.media_type === "IMAGE",
    },
    storage: {
      url: feedItem.media_url,
    },
    description: feedItem.caption,
    asset_id: feedItem.id,
  } as CollectionFile;
};

export const stockImageToCollectionFile = (
  item: StockImage
): CollectionFile => {
  return {
    _id: String(item.id),
    name: item.alt || `Photo ${item.id}`,
    thumbnail: item.src.tiny,
    downloadUrl: item.src.original,
    meta: {
      isVideo: false,
      isImage: true,
    },
    storage: {
      url: item.src.original,
    },
    description: item.alt,
    asset_id: String(item.id),
  } as CollectionFile;
};

export const convertFileToFeed = (file: CollectionFile) => ({
  id: file._id,
  media_url: file.downloadUrl,
  permalink: file.downloadUrl,
  caption: file.description,
  username: "unknow",
});

export const convertFileToStockImage = (file: CollectionFile) => ({
  id: file._id,
  alt: file.description,
  url: file.downloadUrl,
  src: {
    original: file.downloadUrl,
  },
});

/**
 * Safely extracts the file format as a lowercase string.
 *
 * The `meta.format` field has different shapes depending on file type:
 * - For images: string (e.g., "jpeg", "png")
 * - For videos: object (e.g., { format_name: "mov,mp4,m4a,3gp,3g2,mj2", duration: "10.029", ... })
 *
 * This function normalizes both cases to return a simple lowercase format string.
 */
export function getFileFormat(meta: CollectionFile["meta"]): string {
  const format = meta?.format;

  // Handle string format (typically images)
  if (typeof format === "string") {
    return format.toLowerCase();
  }

  // Handle object format (typically videos)
  // The format_name field contains comma-separated formats like "mov,mp4,m4a,3gp,3g2,mj2"
  if (typeof format === "object" && format !== null) {
    const formatObj = format as { format_name?: string; mime?: string };
    if (formatObj.format_name) {
      return formatObj.format_name.split(",")[0].toLowerCase();
    }
    if (formatObj.mime) {
      // Extract format from mime type (e.g., "video/mp4" -> "mp4")
      return formatObj.mime.split("/").pop()?.toLowerCase() || "";
    }
  }

  // Fallback to extracting format from mime type
  if (meta?.mime) {
    const mimeParts = meta.mime.split("/");
    if (mimeParts.length > 1) {
      return mimeParts[1].toLowerCase();
    }
  }

  return "";
}
